/* eslint-disable max-lines, import/max-dependencies */
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { SearchInput } from "@src/appV2/redesign/components/SearchInput/SearchInput";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";

import { useUpdateDefaultShiftTimes } from "../../api/useUpdateDefaultShiftTimes";
import { minorToMajor } from "../../ShiftDetails/components/chargeRateUtils";
import { EditShiftTimeDialog } from "../EditShiftTimeModal/EditShiftTimeDialog";
import { APPLY_TO_OPTIONS, type EditTimeResult } from "../EditShiftTimeModal/types";
import { EditInviteChargeRateDialog } from "./EditInviteChargeRateDialog";
import { useInviteFlow } from "./hooks/useInviteFlow";
import { useProfessionalSearch } from "./hooks/useProfessionalSearch";
import { InviteBlockedModal } from "./InviteBlockedModal";
import { InviteErrorModal } from "./InviteErrorModal";
import { InviteShiftCard } from "./InviteShiftCard";
import { InviteWarningModal } from "./InviteWarningModal";
import { PostAsOpenShiftInfoModal } from "./PostAsOpenShiftInfoModal";
import { ProfessionalList } from "./ProfessionalList";
import { type Professional, type ShiftDetails } from "./types";

interface EditedShiftTimes {
  startTime: Date;
  endTime: Date;
  qualification: string;
  timeSlot: string;
}

interface InviteDrawerContentProps {
  shiftDetails: ShiftDetails;
  /**
   * When provided, syncs time edits from an external source (e.g. PostingFormContext
   * in DailyView). The chat invite flow does not use this.
   */
  lastEditedTimes?: EditedShiftTimes;
  /**
   * When provided, the invite flow starts with this professional already selected
   * (e.g. when the invite is opened from a chat or My Professionals row).
   */
  preselectedProfessional?: Professional;
  /**
   * Optional content rendered at the top of the scrollable area, above the search
   * input. Used by the chat invite flow to host the date/time-slot picker without
   * breaking the "send button always visible" contract on short viewports.
   */
  headerContent?: ReactNode;
  onClose: () => void;
  onSuccess: () => void;
}

export function InviteDrawerContent(props: InviteDrawerContentProps) {
  const {
    shiftDetails,
    lastEditedTimes,
    preselectedProfessional,
    headerContent,
    onClose,
    onSuccess,
  } = props;

  const postToMarketplaceInfoModalState = useModalState(ModalStatus.CLOSED);
  const editTimeModalState = useModalState(ModalStatus.CLOSED);
  const editChargeRateModalState = useModalState(ModalStatus.CLOSED);

  const [editedStartTime, setEditedStartTime] = useState<Date>(shiftDetails.startTime);
  const [editedEndTime, setEditedEndTime] = useState<Date>(shiftDetails.endTime);

  useEffect(() => {
    setEditedStartTime(shiftDetails.startTime);
    setEditedEndTime(shiftDetails.endTime);
  }, [shiftDetails.startTime, shiftDetails.endTime]);

  // Sync with externally edited times (e.g. from PostingFormContext in DailyView)
  useEffect(() => {
    if (
      lastEditedTimes &&
      lastEditedTimes.qualification === shiftDetails.qualification &&
      lastEditedTimes.timeSlot === shiftDetails.timeSlot
    ) {
      setEditedStartTime(lastEditedTimes.startTime);
      setEditedEndTime(lastEditedTimes.endTime);
    }
  }, [lastEditedTimes, shiftDetails.qualification, shiftDetails.timeSlot]);

  const effectiveShiftDetails: ShiftDetails = {
    ...shiftDetails,
    startTime: editedStartTime,
    endTime: editedEndTime,
  };

  const hasPreselectedProfessional = isDefined(preselectedProfessional);

  const {
    searchQuery,
    isSearching,
    allProfessionals,
    starredProfessionals,
    recentProfessionals,
    setSearchQuery,
  } = useProfessionalSearch({
    workplaceId: shiftDetails.workplaceId,
    qualification: shiftDetails.qualification,
    skip: hasPreselectedProfessional,
  });

  const {
    status,
    selectedProfessional,
    postToMarketplace,
    blockReason,
    canSendInvite,
    errorReasons,
    warningReasons,
    missingDocuments,
    errorDetail,
    rushFeeInfo,
    startTimeError,
    errorModalState,
    warningModalState,
    blockedModalState,
    baseChargeRate,
    chargeConstraints,
    customChargeRateBoost,
    isCustomRateBoostEnabled,
    isPricingLoading,
    setCustomChargeRateBoost,
    selectProfessional,
    clearSelection,
    setPostToMarketplace,
    sendInvite,
    proceedWithWarning,
    checkIfBlocked,
    dismissError,
  } = useInviteFlow({
    shiftDetails: effectiveShiftDetails,
    preselectedProfessional,
    onSuccess,
  });

  const { mutateAsync: updateDefaultShiftTimes } = useUpdateDefaultShiftTimes();

  const baseRateInDollars = isDefined(baseChargeRate)
    ? minorToMajor(baseChargeRate.amountInMinorUnits)
    : undefined;

  const showChargeRate = isCustomRateBoostEnabled && isDefined(selectedProfessional);

  const chargeRateDisplay =
    showChargeRate && isDefined(baseRateInDollars)
      ? `${formatDollarsAsUsd(baseRateInDollars + (customChargeRateBoost ?? 0), { trailingZeroDisplay: "stripIfInteger" })}/hr`
      : undefined;

  const baseChargeRateDisplay =
    showChargeRate && isDefined(baseRateInDollars)
      ? `${formatDollarsAsUsd(baseRateInDollars, { trailingZeroDisplay: "stripIfInteger" })}/hr`
      : undefined;

  const boostRateDisplay =
    showChargeRate && isDefined(customChargeRateBoost) && customChargeRateBoost > 0
      ? `${formatDollarsAsUsd(customChargeRateBoost, { trailingZeroDisplay: "stripIfInteger" })}/hr`
      : undefined;

  useEffect(() => {
    checkIfBlocked();
  }, [checkIfBlocked]);

  const handleBlockedModalClose = useCallback(() => {
    blockedModalState.closeModal();
    onClose();
  }, [blockedModalState, onClose]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
    },
    [setSearchQuery]
  );

  const handleEditTimeSave = useCallback(
    async (result: EditTimeResult) => {
      setEditedStartTime(result.start);
      setEditedEndTime(result.end);

      if (isDefined(shiftDetails.timezone) && result.applyTo !== APPLY_TO_OPTIONS.THIS_SHIFT_ONLY) {
        const isApplyToAll = result.applyTo === APPLY_TO_OPTIONS.ALL_SHIFTS;
        await updateDefaultShiftTimes({
          workplaceId: shiftDetails.workplaceId,
          timeSlot: shiftDetails.timeSlot,
          startTime: result.start,
          endTime: result.end,
          qualification: isApplyToAll ? undefined : shiftDetails.qualification,
          currentQualification: isApplyToAll ? shiftDetails.qualification : undefined,
          timezone: shiftDetails.timezone,
        });
      }
    },
    [updateDefaultShiftTimes, shiftDetails]
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {headerContent}
          {!hasPreselectedProfessional && (
            <>
              <Box
                sx={(theme) => ({
                  padding: 5,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: theme.palette.background?.standout,
                })}
              >
                <SearchInput
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional for drawer UX
                  autoFocus
                  isLoading={isSearching}
                  placeholder="Search professionals..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Box>
              <ProfessionalList
                allProfessionals={allProfessionals}
                isSearching={isSearching}
                recentProfessionals={recentProfessionals}
                searchQuery={searchQuery}
                selectedProfessional={selectedProfessional}
                starredProfessionals={starredProfessionals}
                status={status}
                onSelectProfessional={selectProfessional}
              />
            </>
          )}
        </Box>

        <Box
          sx={(theme) => ({
            padding: theme.spacing(5),
            borderTop: `1px solid ${theme.palette.border?.base}`,
            backgroundColor: theme.palette.background?.standout,
            flexShrink: 0,
            // Cap the footer so the search list above keeps ≥5 rows visible
            // after a professional is selected. Without this cap the selected
            // professional + charge-rate expanded state could push the list
            // off-screen on short viewports (ORB-1749).
            maxHeight: "20rem",
            overflowY: "auto",
          })}
        >
          <InviteShiftCard
            baseChargeRateDisplay={baseChargeRateDisplay}
            boostRateDisplay={boostRateDisplay}
            chargeRateDisplay={chargeRateDisplay}
            endTime={editedEndTime}
            errorMessage={startTimeError}
            isChargeRateLoading={isCustomRateBoostEnabled && isPricingLoading}
            isDisabled={!canSendInvite}
            isLoading={status === "sending" || status === "validating"}
            postToMarketplace={postToMarketplace}
            qualificationDisplayName={shiftDetails.qualificationDisplayName}
            selectedProfessional={selectedProfessional}
            startTime={editedStartTime}
            timeSlot={shiftDetails.timeSlot}
            timezone={shiftDetails.timezone}
            onEditChargeRate={
              isCustomRateBoostEnabled && isDefined(baseChargeRate) && isDefined(chargeConstraints)
                ? () => {
                    editChargeRateModalState.openModal();
                  }
                : undefined
            }
            onEditTime={() => {
              editTimeModalState.openModal();
            }}
            onInfoIconClick={() => {
              postToMarketplaceInfoModalState.openModal();
            }}
            onPostToMarketplaceChange={setPostToMarketplace}
            onRemoveProfessional={hasPreselectedProfessional ? undefined : clearSelection}
            onSendInvite={sendInvite}
          />
        </Box>
      </Box>

      <PostAsOpenShiftInfoModal
        modalState={postToMarketplaceInfoModalState}
        onClose={() => {
          postToMarketplaceInfoModalState.closeModal();
        }}
      />

      {blockReason && (
        <InviteBlockedModal
          blockReason={blockReason}
          modalState={blockedModalState}
          onClose={handleBlockedModalClose}
        />
      )}

      <InviteErrorModal
        detail={errorDetail}
        errorReasons={errorReasons}
        missingDocuments={missingDocuments}
        modalState={errorModalState}
        professionalName={selectedProfessional?.name}
        qualificationName={shiftDetails.qualificationDisplayName}
        onDismiss={dismissError}
        onSelectAnother={hasPreselectedProfessional ? undefined : clearSelection}
      />

      <InviteWarningModal
        isLoading={status === "sending"}
        modalState={warningModalState}
        rushFeeInfo={rushFeeInfo}
        warningReasons={warningReasons}
        onGoBack={hasPreselectedProfessional ? undefined : clearSelection}
        onProceed={proceedWithWarning}
      />

      <EditShiftTimeDialog
        validatePastStartTime
        initialTime={{ start: editedStartTime, end: editedEndTime }}
        modalState={editTimeModalState}
        qualificationName={shiftDetails.qualificationDisplayName}
        timeSlot={shiftDetails.timeSlot}
        timezone={shiftDetails.timezone}
        onSave={handleEditTimeSave}
      />

      {isCustomRateBoostEnabled &&
        isDefined(baseChargeRate) &&
        isDefined(chargeConstraints) &&
        isDefined(selectedProfessional) && (
          <EditInviteChargeRateDialog
            baseChargeRate={baseChargeRate}
            chargeConstraints={chargeConstraints}
            currentBoost={customChargeRateBoost}
            endTime={effectiveShiftDetails.endTime}
            modalState={editChargeRateModalState}
            professionalAvatarUrl={selectedProfessional.avatarUrl}
            professionalName={selectedProfessional.name}
            qualificationDisplayName={shiftDetails.qualificationDisplayName}
            startTime={effectiveShiftDetails.startTime}
            timeSlot={effectiveShiftDetails.timeSlot}
            timezone={effectiveShiftDetails.timezone}
            onSave={setCustomChargeRateBoost}
          />
        )}
    </>
  );
}
/* eslint-enable max-lines, import/max-dependencies */
